export type MobileSubmissionState =
  | 'local_pending'
  | 'upload_session_created'
  | 'media_uploaded'
  | 'proof_finalized'
  | 'acknowledged'
  | 'retryable_failure'
  | 'action_required'
  | 'conflict';

export type SubmissionOperationSnapshot = {
  operationId: string;
  operationType: 'media' | 'task_proof' | 'signoff';
  state: MobileSubmissionState;
};

export type SubmissionAggregateSnapshot = {
  state: MobileSubmissionState;
  expectedOperationCount: number;
  acknowledgedOperationCount: number;
  safe: boolean;
};

const PROGRESS_ORDER: MobileSubmissionState[] = [
  'local_pending',
  'upload_session_created',
  'media_uploaded',
  'proof_finalized',
  'acknowledged',
];

export function isMobileSubmissionState(value: unknown): value is MobileSubmissionState {
  return typeof value === 'string' && (
    PROGRESS_ORDER.includes(value as MobileSubmissionState) ||
    value === 'retryable_failure' ||
    value === 'action_required' ||
    value === 'conflict'
  );
}

export function deriveSubmissionAggregate(input: {
  manifestSealed: boolean;
  operations: SubmissionOperationSnapshot[];
  inheritedSignoffAcknowledged?: boolean;
}): SubmissionAggregateSnapshot {
  const expectedOperationCount = input.operations.length;
  const acknowledgedOperationCount = input.operations.filter(
    operation => operation.state === 'acknowledged',
  ).length;

  if (input.operations.some(operation => operation.state === 'conflict')) {
    return { state: 'conflict', expectedOperationCount, acknowledgedOperationCount, safe: false };
  }
  if (input.operations.some(operation => operation.state === 'action_required')) {
    return { state: 'action_required', expectedOperationCount, acknowledgedOperationCount, safe: false };
  }
  if (input.operations.some(operation => operation.state === 'retryable_failure')) {
    return { state: 'retryable_failure', expectedOperationCount, acknowledgedOperationCount, safe: false };
  }

  const hasAcknowledgedSignoff = Boolean(input.inheritedSignoffAcknowledged) || input.operations.some(
    operation => operation.operationType === 'signoff' && operation.state === 'acknowledged',
  );
  const allAcknowledged = expectedOperationCount > 0 && acknowledgedOperationCount === expectedOperationCount;
  if (input.manifestSealed && hasAcknowledgedSignoff && allAcknowledged) {
    return { state: 'acknowledged', expectedOperationCount, acknowledgedOperationCount, safe: true };
  }

  if (input.operations.some(operation =>
    operation.operationType !== 'media' &&
    (operation.state === 'proof_finalized' || operation.state === 'acknowledged')
  )) {
    return { state: 'proof_finalized', expectedOperationCount, acknowledgedOperationCount, safe: false };
  }
  if (input.operations.some(operation =>
    operation.operationType === 'media' &&
    (operation.state === 'media_uploaded' || operation.state === 'acknowledged')
  )) {
    return { state: 'media_uploaded', expectedOperationCount, acknowledgedOperationCount, safe: false };
  }
  if (input.operations.some(operation => operation.state === 'upload_session_created')) {
    return { state: 'upload_session_created', expectedOperationCount, acknowledgedOperationCount, safe: false };
  }

  return { state: 'local_pending', expectedOperationCount, acknowledgedOperationCount, safe: false };
}

export type OfficeResolutionAction = 'retry' | 'discard' | 'supersede';

const EXCEPTIONAL_SUBMISSION_STATES = new Set<MobileSubmissionState>([
  'retryable_failure',
  'action_required',
  'conflict',
]);

/**
 * Aggregate refreshes are derived from child rows, but some failures occur
 * before a child can be persisted. Preserve those parent-only exceptional
 * states until an audited office decision changes them.
 */
export function resolveSubmissionStateForAggregateRefresh(input: {
  currentState: MobileSubmissionState;
  derivedState: MobileSubmissionState;
  operationCount: number;
  lastErrorCode?: string | null;
}): MobileSubmissionState {
  if (input.operationCount !== 0) return input.derivedState;
  if (EXCEPTIONAL_SUBMISSION_STATES.has(input.currentState)) return input.currentState;
  if (input.lastErrorCode === 'ASSIGNMENT_NOT_AVAILABLE') return 'conflict';
  return input.derivedState;
}

/**
 * A retry may be requested for any incomplete submission, not only exceptional
 * ones: a client-side failure (e.g. a signoff that exhausted local retries
 * before its operation was ever persisted server-side) can strand a submission
 * in an in-flight state with no exceptional operations to act on.
 * Discard/supersede remain restricted to exceptional states.
 */
export function canOfficeResolveSubmission(
  state: MobileSubmissionState,
  action: OfficeResolutionAction,
): boolean {
  switch (state) {
    case 'retryable_failure':
    case 'action_required':
    case 'conflict':
      return true;
    case 'local_pending':
    case 'upload_session_created':
    case 'media_uploaded':
    case 'proof_finalized':
      return action === 'retry';
    case 'acknowledged':
      return false;
    default: {
      const exhaustive: never = state;
      throw new Error(`Unhandled submission state: ${exhaustive}`);
    }
  }
}

export function isMonotonicSubmissionTransition(
  from: MobileSubmissionState,
  to: MobileSubmissionState,
): boolean {
  if (from === to) return true;
  if (from === 'acknowledged') return false;
  if (to === 'retryable_failure' || to === 'action_required' || to === 'conflict') return true;
  if (from === 'retryable_failure') return to !== 'local_pending';
  if (from === 'action_required' || from === 'conflict') return false;

  const fromIndex = PROGRESS_ORDER.indexOf(from);
  const toIndex = PROGRESS_ORDER.indexOf(to);
  return fromIndex >= 0 && toIndex >= fromIndex;
}

export function deriveExpiredUploadFailure(
  previousFailures: number,
  maximumAutomaticFailures = 5,
): { attempts: number; state: 'retryable_failure' | 'action_required' } {
  const attempts = Math.max(0, Math.floor(previousFailures)) + 1;
  return {
    attempts,
    state: attempts >= maximumAutomaticFailures ? 'action_required' : 'retryable_failure',
  };
}

export const RECONCILIATION_CANDIDATE_ATTEMPTS_PREFIX = 'candidate_attempts=';

export function parseReconciliationCandidateAttempts(lastErrorMessage: string | null): number {
  if (!lastErrorMessage) {
    return 0;
  }

  const match = lastErrorMessage.match(/candidate_attempts=(\d+)/);
  return match ? Number.parseInt(match[1], 10) || 0 : 0;
}

export function formatReconciliationCandidateFailureMessage(attempts: number, error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return `${RECONCILIATION_CANDIDATE_ATTEMPTS_PREFIX}${attempts}; ${message}`;
}

export function buildReconciliationCandidateAggregateUpdate(
  previousLastErrorMessage: string | null,
  resolvedAt: Date | null,
  error: unknown,
): {
  lastErrorCode: string;
  lastErrorMessage: string;
  updatedAt: Date;
  actionRequired: boolean;
  state?: MobileSubmissionState;
} {
  const failure = deriveExpiredUploadFailure(
    parseReconciliationCandidateAttempts(previousLastErrorMessage),
  );
  const update = {
    lastErrorCode: 'RECONCILIATION_CANDIDATE_FAILED',
    lastErrorMessage: formatReconciliationCandidateFailureMessage(failure.attempts, error),
    updatedAt: new Date(),
    actionRequired: failure.state === 'action_required',
  };

  if (failure.state === 'action_required' && resolvedAt == null) {
    return { ...update, state: 'action_required' };
  }

  return update;
}

export const OFFICE_RETRY_REQUESTED_ERROR_CODE = 'OFFICE_RETRY_REQUESTED';

export function shouldSkipStaleReconciliationFailureSnapshot(
  observedUpdatedAt: Date,
  authoritative: {
    updatedAt: Date;
    lastErrorCode: string | null;
  },
): boolean {
  if (authoritative.updatedAt.getTime() <= observedUpdatedAt.getTime()) {
    return false;
  }

  return authoritative.lastErrorCode === OFFICE_RETRY_REQUESTED_ERROR_CODE;
}
